import { getCountry, getLanguage } from '../utils/i18n';
import { queryString } from '../utils/queryString';
import config, { Country, Language } from '.';

export type WebRoute = {
  href: string;
  as: string;
  auth?: boolean;
  /** Structured query params; redirectTo merges them into the URL. Never embed a query in href/as —
   * redirectTo(route, replace = true) passes href as UrlObject.pathname and Next percent-encodes the '?'. */
  query?: Record<string, string>;
};

/** Formats a WebRoute into a plain URL string (for <a>/Link hrefs that can't take a route object). */
export const webRouteToUrl = (route: WebRoute): string => {
  const params = route.query
    ? Object.entries(route.query)
        .map(([k, v]) => `${k}=${v}`)
        .join('&')
    : '';

  return params ? `${route.as}?${params}` : route.as;
};

export type YagaExternalWebRoute = {
  href: string;
};

type LanguageRoute = Record<Language, WebRoute>;
type CountryRoute = Record<Country, YagaExternalWebRoute>;
type CountryLanguageRoute = Record<Country, Partial<Record<Language, YagaExternalWebRoute>> | YagaExternalWebRoute>;

// Desides which route to show by locale
function withCountry(locales: CountryRoute) {
  const localeCode = config.get('country');

  return locales[localeCode];
}

function withCountryAndLanguage(locales: CountryLanguageRoute) {
  const userCountryCode = getCountry() || config.get('country');
  const userLanguageCode = getLanguage() || Language.en;

  return locales[userCountryCode][userLanguageCode] || locales[userCountryCode];
}

// Decides which route to show by user language
function withLanguage(routes: Partial<LanguageRoute>, enforceLanguageCode?: string) {
  // All route translation language codes
  const possibleRouteLanguageCodes = getRouteLanguages();

  // Get route with user selected language is enforce not passed
  const userLanguageCode = enforceLanguageCode || getLanguage() || Language.en;

  // Translate routes using user selected language. Default to english
  return possibleRouteLanguageCodes.includes(userLanguageCode) ? routes[userLanguageCode] : routes.en;
}

export function getRouteLanguages() {
  const routeLanguages = config.get('languageRoutes');

  if (!routeLanguages?.length) {
    throw new Error('Routes language config is missing!');
  }

  return routeLanguages;
}

export const INDEX = (): WebRoute => ({
  href: '/',
  as: '/',
});

export const AUTH = (): WebRoute => ({
  href: '/auth',
  as: '/auth',
});

export const SEARCH = (search: string, enforceLanguage?: Language): WebRoute => {
  search = search && encodeURIComponent(search?.trim());

  return withLanguage(
    {
      [Language.et]: {
        href: `/otsi${search ? '/[search]' : ''}`,
        as: `/otsi/${search}`,
      },
      [Language.en]: {
        href: `/search${search ? '/[search]' : ''}`,
        as: `/search/${search}`,
      },
    },
    enforceLanguage,
  );
};

export const PRODUCT_SEARCH = (search: string, enforceLanguage?: Language): WebRoute => {
  search = search && encodeURIComponent(search?.trim());

  return withLanguage(
    {
      [Language.et]: {
        href: `/otsi${search ? '/[search]' : ''}?section=products`,
        as: `/otsi/${search}?section=products`,
      },
      [Language.en]: {
        href: `/search${search ? '/[search]' : ''}?section=products`,
        as: `/search/${search}?section=products`,
      },
    },
    enforceLanguage,
  );
};

export const SHOPS = (enforceLanguage?: Language): WebRoute =>
  withLanguage(
    {
      [Language.et]: {
        href: '/poed',
        as: '/poed',
      },
      [Language.en]: {
        href: '/shops',
        as: '/shops',
      },
    },
    enforceLanguage,
  );

export const SHOP = (shopSlug: string): WebRoute => ({
  href: '/[shop-slug]',
  as: `/${shopSlug}`,
});

export const PRODUCT = (
  shopSlug: string,
  productSlug: string,
  enforceLanguage?: Language,
  indexInList?: number,
  sourceModule?: string,
  searchGuid?: string,
  recommendationId?: string,
  recommendationEngine?: string,
): WebRoute => {
  const query = buildProductQuery(indexInList, sourceModule, searchGuid, recommendationId, recommendationEngine);

  return withLanguage(
    {
      [Language.et]: {
        href: `/[shop-slug]/toode/[product-slug]${query}`,
        as: `/${shopSlug}/toode/${productSlug}${query}`,
      },
      [Language.en]: {
        href: `/[shop-slug]/product/[product-slug]${query}`,
        as: `/${shopSlug}/product/${productSlug}${query}`,
      },
    },
    enforceLanguage,
  );
};

export const PRODUCT_EDIT = (shopSlug: string, productSlug: string, enforceLanguage?: Language): WebRoute =>
  withLanguage(
    {
      [Language.et]: {
        href: '/[shop-slug]/toode/[product-slug]/muuda',
        as: `/${shopSlug}/toode/${productSlug}/muuda`,
      },
      [Language.en]: {
        href: '/[shop-slug]/product/[product-slug]/edit',
        as: `/${shopSlug}/product/${productSlug}/edit`,
      },
    },
    enforceLanguage,
  );

export const PRODUCTS = (enforceLanguage?: Language): WebRoute =>
  withLanguage(
    {
      [Language.et]: {
        href: '/lemmikud',
        as: '/lemmikud',
      },
      [Language.en]: {
        href: '/favourites',
        as: '/favourites',
      },
    },
    enforceLanguage,
  );

// Categories have one official URL, the English slug under /category/, for every
// language. The Estonian /kategooria/ pages still exist but only to 301 onto it, so
// generating them here would put a redirect in front of every in-app category click.
export const CATEGORY = (categoryName: string): WebRoute => ({
  href: '/category/[category-name]',
  as: `/category/${categoryName}`,
});

export const CATEGORY_PRODUCTS = (categoryName: string): WebRoute => ({
  href: '/category/[category-name]/products',
  as: `/category/${categoryName}/products`,
});

export const BRAND = (brandName: string, enforceLanguage?: Language): WebRoute =>
  withLanguage(
    {
      [Language.et]: {
        href: '/brand/[brand-name]',
        as: `/brand/${brandName}`,
      },
      [Language.en]: {
        href: '/brand/[brand-name]',
        as: `/brand/${brandName}`,
      },
    },
    enforceLanguage,
  );

export const NOTIFICATIONS = (enforceLanguage?: Language): WebRoute =>
  withLanguage(
    {
      [Language.et]: {
        href: '/teavitused',
        as: '/teavitused',
      },
      [Language.en]: {
        href: '/notifications',
        as: '/notifications',
      },
    },
    enforceLanguage,
  );

export const BUY_PRODUCT = (shopSlug: string, productSlug: string, enforceLanguage?: Language, sourceModule?: string): WebRoute => {
  const query = buildProductQuery(undefined, sourceModule);

  return withLanguage(
    {
      [Language.et]: {
        href: `/[shop-slug]/toode/[product-slug]/osta${query}`,
        as: `/${shopSlug}/toode/${productSlug}/osta${query}`,
      },
      [Language.en]: {
        href: `/[shop-slug]/product/[product-slug]/buy${query}`,
        as: `/${shopSlug}/product/${productSlug}/buy${query}`,
      },
    },
    enforceLanguage,
  );
};

export const BUY_PRODUCT_SUCCESS = (shopSlug: string, productSlug: string, enforceLanguage?: Language): WebRoute =>
  withLanguage(
    {
      [Language.et]: {
        href: '/[shop-slug]/toode/[product-slug]/osta/sooritatud',
        as: `/${shopSlug}/toode/${productSlug}/osta/sooritatud`,
      },
      [Language.en]: {
        href: '/[shop-slug]/product/[product-slug]/buy/success',
        as: `/${shopSlug}/product/${productSlug}/buy/success`,
      },
    },
    enforceLanguage,
  );

export const BUY_PRODUCT_CANCEL = (shopSlug: string, productSlug: string, enforceLanguage?: Language): WebRoute =>
  withLanguage(
    {
      [Language.et]: {
        href: '/[shop-slug]/toode/[product-slug]/osta/katkestatud',
        as: `/${shopSlug}/toode/${productSlug}/osta/katkestatud`,
      },
      [Language.en]: {
        href: '/[shop-slug]/product/[product-slug]/buy/cancel',
        as: `/${shopSlug}/product/${productSlug}/buy/cancel`,
      },
    },
    enforceLanguage,
  );

export const BUY_PRODUCT_PROCESSING = (shopSlug: string, productSlug: string, enforceLanguage?: Language): WebRoute =>
  withLanguage(
    {
      [Language.et]: {
        href: '/[shop-slug]/toode/[product-slug]/osta/tootlemine',
        as: `/${shopSlug}/toode/${productSlug}/osta/tootlemine`,
      },
      [Language.en]: {
        href: '/[shop-slug]/product/[product-slug]/buy/processing',
        as: `/${shopSlug}/product/${productSlug}/buy/processing`,
      },
    },
    enforceLanguage,
  );

export const CHECKOUT_PROCESSING = (gateway: string) => ({
  href: '/checkout/processing/[gateway]',
  as: `/checkout/processing/${gateway}`,
});

export const CHECKOUT_SUCCESS = () => ({
  href: '/checkout/success',
  as: '/checkout/success',
});

export const CHECKOUT_CANCEL = () => ({
  href: '/checkout/cancel',
  as: '/checkout/cancel',
});

export const MY = {
  SHOP: (enforceLanguage?: Language): WebRoute =>
    withLanguage(
      {
        [Language.et]: {
          href: '/minu/pood',
          as: '/minu/pood',
        },
        [Language.en]: {
          href: '/my/shop',
          as: '/my/shop',
        },
      },
      enforceLanguage,
    ),
  // former vouchers, aka coupon
  DISCOUNT_CODES: (enforceLanguage?: Language): WebRoute =>
    withLanguage(
      {
        [Language.et]: {
          href: '/minu/sooduskoodid',
          as: '/minu/sooduskoodid',
        },
        [Language.en]: {
          href: '/my/discount-codes',
          as: '/my/discount-codes',
        },
      },
      enforceLanguage,
    ),
  PROMOCODES: (enforceLanguage?: Language): WebRoute =>
    withLanguage(
      {
        [Language.et]: {
          href: '/minu/promokoodid',
          as: '/minu/promokoodid',
        },
        [Language.en]: {
          href: '/my/promocodes',
          as: '/my/promocodes',
        },
      },
      enforceLanguage,
    ),
  PROMOCODES_ENTERCODE: (enforceLanguage?: Language): WebRoute =>
    withLanguage(
      {
        [Language.et]: {
          href: '/minu/promokoodid/sisestakood',
          as: '/minu/promokoodid/sisestakood',
        },
        [Language.en]: {
          href: '/my/promocodes/entercode',
          as: '/my/promocodes/entercode',
        },
      },
      enforceLanguage,
    ),
  PROMOCODES_INVITE: (enforceLanguage?: Language): WebRoute =>
    withLanguage(
      {
        [Language.et]: {
          href: '/minu/promokoodid/kutsu',
          as: '/minu/promokoodid/kutsu',
        },
        [Language.en]: {
          href: '/my/promocodes/invite',
          as: '/my/promocodes/invite',
        },
      },
      enforceLanguage,
    ),
  ORDERS: (enforceLanguage?: Language): WebRoute =>
    withLanguage(
      {
        [Language.et]: {
          href: '/minu/tellimused',
          as: '/minu/tellimused',
        },
        [Language.en]: {
          href: '/my/orders',
          as: '/my/orders',
        },
      },
      enforceLanguage,
    ),
  ORDER: (orderId: number, enforceLanguage?: Language): WebRoute =>
    withLanguage(
      {
        [Language.et]: {
          href: '/minu/tellimused/[order-id]',
          as: `/minu/tellimused/${orderId}`,
        },
        [Language.en]: {
          href: '/my/orders/[order-id]',
          as: `/my/orders/${orderId}`,
        },
      },
      enforceLanguage,
    ),
  PURCHASES: (enforceLanguage?: Language): WebRoute =>
    withLanguage(
      {
        [Language.et]: {
          href: '/minu/ostud',
          as: '/minu/ostud',
        },
        [Language.en]: {
          href: '/my/purchases',
          as: '/my/purchases',
        },
      },
      enforceLanguage,
    ),
  PURCHASE: (purchaseId: number, enforceLanguage?: Language): WebRoute =>
    withLanguage(
      {
        [Language.et]: {
          href: '/minu/ostud/[purchase-id]',
          as: `/minu/ostud/${purchaseId}`,
        },
        [Language.en]: {
          href: '/my/purchases/[purchase-id]',
          as: `/my/purchases/${purchaseId}`,
        },
      },
      enforceLanguage,
    ),
  WALLET: (enforceLanguage?: Language): WebRoute =>
    withLanguage(
      {
        [Language.et]: {
          href: '/minu/konto',
          as: '/minu/konto',
        },
        [Language.en]: {
          href: '/my/wallet',
          as: '/my/wallet',
        },
      },
      enforceLanguage,
    ),
  SETTINGS: (enforceLanguage?: Language): WebRoute =>
    withLanguage(
      {
        [Language.et]: {
          href: '/minu/seaded',
          as: '/minu/seaded',
        },
        [Language.en]: {
          href: '/my/settings',
          as: '/my/settings',
        },
      },
      enforceLanguage,
    ),
  SETTINGS_PROFILE: (enforceLanguage?: Language): WebRoute =>
    withLanguage(
      {
        [Language.et]: {
          href: '/minu/seaded/profiil',
          as: '/minu/seaded/profiil',
        },
        [Language.en]: {
          href: '/my/settings/profile',
          as: '/my/settings/profile',
        },
      },
      enforceLanguage,
    ),
  SETTINGS_PROFILE_DAC7: (enforceLanguage?: Language): WebRoute =>
    withLanguage(
      {
        [Language.et]: {
          href: '/minu/seaded/profiil/DAC7',
          as: '/minu/seaded/profiil/DAC7',
        },
        [Language.en]: {
          href: '/my/settings/profile/DAC7',
          as: '/my/settings/profile/DAC7',
        },
      },
      enforceLanguage,
    ),
  SETTINGS_PROFILE_DAC7_SUCCESS: (enforceLanguage?: Language): WebRoute =>
    withLanguage(
      {
        [Language.et]: {
          href: '/minu/seaded/profiil/DAC7/success',
          as: '/minu/seaded/profiil/DAC7/success',
        },
        [Language.en]: {
          href: '/my/settings/profile/DAC7/success',
          as: '/my/settings/profile/DAC7/success',
        },
      },
      enforceLanguage,
    ),
  SETTINGS_BANK: (enforceLanguage?: Language): WebRoute =>
    withLanguage(
      {
        [Language.et]: {
          href: '/minu/seaded/pank',
          as: '/minu/seaded/pank',
        },
        [Language.en]: {
          href: '/my/settings/bank',
          as: '/my/settings/bank',
        },
      },
      enforceLanguage,
    ),
  SETTINGS_NOTIFICATIONS: (enforceLanguage?: Language): WebRoute =>
    withLanguage(
      {
        [Language.et]: {
          href: '/minu/seaded/teavitused',
          as: '/minu/seaded/teavitused',
        },
        [Language.en]: {
          href: '/my/settings/notifications',
          as: '/my/settings/notifications',
        },
      },
      enforceLanguage,
    ),
  SETTINGS_ADDRESS: (enforceLanguage?: Language): WebRoute =>
    withLanguage(
      {
        [Language.et]: {
          href: '/minu/seaded/kontaktandmed',
          as: '/minu/seaded/kontaktandmed',
        },
        [Language.en]: {
          href: '/my/settings/address',
          as: '/my/settings/address',
        },
      },
      enforceLanguage,
    ),
  SETTINGS_PRIVACY: (enforceLanguage?: Language): WebRoute =>
    withLanguage(
      {
        [Language.et]: {
          href: '/minu/seaded/privaatsus',
          as: '/minu/seaded/privaatsus',
        },
        [Language.en]: {
          href: '/my/settings/privacy',
          as: '/my/settings/privacy',
        },
      },
      enforceLanguage,
    ),
  SETTINGS_LANGUAGE: (enforceLanguage?: Language): WebRoute =>
    withLanguage(
      {
        [Language.et]: {
          href: '/minu/seaded/keel',
          as: '/minu/seaded/keel',
        },
        [Language.en]: {
          href: '/my/settings/language',
          as: '/my/settings/language',
        },
      },
      enforceLanguage,
    ),
  MESSAGES: (enforceLanguage?: Language): WebRoute =>
    withLanguage(
      {
        [Language.et]: {
          href: '/minu/sonumid',
          as: '/minu/sonumid',
        },
        [Language.en]: {
          href: '/my/messages',
          as: '/my/messages',
        },
      },
      enforceLanguage,
    ),
  CONVERSATION: (shopSlug: string, enforceLanguage?: Language): WebRoute =>
    withLanguage(
      {
        [Language.et]: {
          href: '/minu/sonumid/[shop-slug]',
          as: `/minu/sonumid/${shopSlug}`,
        },
        [Language.en]: {
          href: '/my/messages/[shop-slug]',
          as: `/my/messages/${shopSlug}`,
        },
      },
      enforceLanguage,
    ),
  CONVERSATION_WITH_PRODUCT: (shopSlug: string, productSlug: string, enforceLanguage?: Language): WebRoute =>
    withLanguage(
      {
        [Language.et]: {
          href: '/minu/sonumid/[shop-slug]/[product-slug]',
          as: `/minu/sonumid/${shopSlug}/${productSlug}`,
        },
        [Language.en]: {
          href: '/my/messages/[shop-slug]/[product-slug]',
          as: `/my/messages/${shopSlug}/${productSlug}`,
        },
      },
      enforceLanguage,
    ),
};

export const FAQ = (enforceLanguage?: Language): WebRoute =>
  withLanguage(
    {
      [Language.et]: {
        href: '/kkk',
        as: '/kkk',
      },
      [Language.en]: {
        href: '/faq',
        as: '/faq',
      },
    },
    enforceLanguage,
  );

// DEV-2305: entryPoint tags where the Add Listing flow was opened from; it lands on
// item_listing_start as entry_point. Omit it for non-navigation uses (e.g. route matching).
export const SELL_ADD_PRODUCT = (enforceLanguage?: Language, entryPoint?: string): WebRoute => {
  const route = withLanguage(
    {
      [Language.et]: {
        href: '/muuk/lisa-toode',
        as: '/muuk/lisa-toode',
      },
      [Language.en]: {
        href: '/sell/add-product',
        as: '/sell/add-product',
      },
    },
    enforceLanguage,
  );

  return entryPoint ? { ...route, query: { entry_point: entryPoint } } : route;
};

export const SELL_ADD_PRODUCT_SUCCESS = (productSlug: string, enforceLanguage?: Language): WebRoute =>
  withLanguage(
    {
      [Language.et]: {
        href: '/muuk/lisa-toode/[product-slug]',
        as: `/muuk/lisa-toode/${productSlug}`,
      },
      [Language.en]: {
        href: '/sell/add-product/[product-slug]',
        as: `/sell/add-product/${productSlug}`,
      },
    },
    enforceLanguage,
  );

export const START_EARNING_EXTRA = (enforceLanguage?: Language): WebRoute =>
  withLanguage(
    {
      [Language.et]: {
        href: null, // doesn't exist for now
        // href: '/start-earning-extra',
        as: '/start-earning-extra',
      },
      [Language.en]: {
        href: '/start-earning-extra',
        as: '/start-earning-extra',
      },
    },
    enforceLanguage,
  );

export const BUY_SECONDHAND_FASHION = (enforceLanguage?: Language): WebRoute =>
  withLanguage(
    {
      [Language.et]: {
        href: null, // doesn't exist for now
        // href: '/buy-secondhand-fashion',
        as: '/buy-secondhand-fashion',
      },
      [Language.en]: {
        href: '/buy-secondhand-fashion',
        as: '/buy-secondhand-fashion',
      },
    },
    enforceLanguage,
  );

export const START_EARNING_EXTRA2 = (enforceLanguage?: Language): WebRoute =>
  withLanguage(
    {
      [Language.et]: {
        href: null, // doesn't exist for now
        // href: '/start-earning-extra',
        as: '/start-earning-extra-2',
      },
      [Language.en]: {
        href: '/start-earning-extra-2',
        as: '/start-earning-extra-2',
      },
    },
    enforceLanguage,
  );

export const BUY_SECONDHAND_FASHION2 = (enforceLanguage?: Language): WebRoute =>
  withLanguage(
    {
      [Language.et]: {
        href: null, // doesn't exist for now
        // href: '/buy-secondhand-fashion',
        as: '/buy-secondhand-fashion-2',
      },
      [Language.en]: {
        href: '/buy-secondhand-fashion-2',
        as: '/buy-secondhand-fashion-2',
      },
    },
    enforceLanguage,
  );

export const YAGA_EXTERNALS = {
  SUPPORT: (queryParams?: Record<string, unknown>): YagaExternalWebRoute =>
    withCountryAndLanguage({
      [Country.EE]: {
        [Language.et]: {
          href: `https://kasutajatugi.yaga.ee/hc/et-ee/${queryString(queryParams)}`,
        },
        [Language.en]: {
          href: `https://kasutajatugi.yaga.ee/hc/en-ee/${queryString(queryParams)}`,
        },
      },
      [Country.LV]: {
        [Language.lv]: {
          href: `https://support.shopyaga.lv/hc/lv-lv/${queryString(queryParams)}`,
        },
        [Language.en]: {
          href: `https://support.shopyaga.lv/hc/en-lv/${queryString(queryParams)}`,
        },
      },
      [Country.ZA]: {
        href: `https://support.yaga.co.za/hc/en-us/${queryString(queryParams)}`,
      },
      [Country.KE]: {
        href: `https://support.yaga.co.ke/hc/en-us/${queryString(queryParams)}`,
      },
      [Country.EG]: {
        href: `https://support.yaga-egypt.com/hc/en-us/${queryString(queryParams)}`,
      },
    }),
  BLOG: (): YagaExternalWebRoute =>
    withCountry({
      [Country.EE]: {
        href: 'https://shop.yaga.ee/blogi',
      },
      [Country.LV]: {
        href: 'https://shop.yaga.ee/blogi',
      },
      [Country.ZA]: {
        href: 'https://shop.yaga.co.za/blog',
      },
      [Country.KE]: {
        href: 'https://shop.yaga.co.ke/blog',
      },
      [Country.EG]: {
        href: '',
      },
    }),
  SALES: (): YagaExternalWebRoute =>
    withCountryAndLanguage({
      [Country.EE]: {
        href: 'https://shop.yaga.ee/blogi/esimesed-myygid',
      },
      [Country.LV]: {
        [Language.lv]: {
          href: 'https://support.shopyaga.lv/hc/lv-lv/categories/29217365562141-P%C4%81rdev%C4%93jiem',
        },
        [Language.en]: {
          href: 'https://support.shopyaga.lv/hc/en-lv/categories/29217365562141-P%C4%81rdev%C4%93jiem',
        },
      },
      [Country.ZA]: {
        href: 'https://shop.yaga.co.za/blog/first-sales',
      },
      [Country.KE]: {
        href: 'https://shop.yaga.co.ke/blog/first-sales',
      },
      [Country.EG]: {
        href: '',
      },
    }),
  TERMS: (): YagaExternalWebRoute =>
    withCountryAndLanguage({
      [Country.EE]: {
        [Language.et]: {
          href: 'https://kasutajatugi.yaga.ee/article/185-kasutajalepingu-tingimused',
        },
        [Language.en]: {
          href: 'https://kasutajatugi.yaga.ee/hc/en-ee/articles/22782287672733-TERMS-AND-CONDITIONS-OF-USER-AGREEMENT-OF-SHOP-YAGA-PTY-LTD',
        },
        [Language.lv]: {
          href: 'https://support.shopyaga.lv/hc/lv-lv/articles/29754364452381-Noteikumi-Nosac%C4%ABjumi',
        },
      },
      [Country.LV]: {
        [Language.lv]: {
          href: 'https://support.shopyaga.lv/hc/lv-lv/articles/29754364452381-Noteikumi-Nosac%C4%ABjumi',
        },
        [Language.en]: {
          href: 'https://support.shopyaga.lv/hc/en-lv/articles/29754364452381-Terms-Conditions',
        },
      },
      [Country.ZA]: {
        href: 'https://support.yaga.co.za/article/190-terms-and-conditions',
      },
      [Country.KE]: {
        href: 'https://support.yaga.co.ke/article/364-terms-conditions',
      },
      [Country.EG]: {
        href: '/terms-and-conditions',
      },
    }),
  PRIVACY_POLICY: (): YagaExternalWebRoute =>
    withCountryAndLanguage({
      [Country.EE]: {
        [Language.et]: {
          href: 'https://kasutajatugi.yaga.ee/article/186-privaatsuspoliitika',
        },
        [Language.en]: {
          href: 'https://kasutajatugi.yaga.ee/hc/en-ee/articles/22780568264861-Privacy-policy',
        },
        [Language.lv]: {
          href: 'https://support.shopyaga.lv/hc/lv-lv/articles/29754406987549-Privacy-Policy',
        },
      },
      [Country.LV]: {
        [Language.lv]: {
          href: 'https://support.shopyaga.lv/hc/lv-lv/articles/29754406987549-Privacy-Policy',
        },
        [Language.en]: {
          href: 'https://support.shopyaga.lv/hc/en-lv/articles/29754406987549-Privacy-Policy',
        },
      },
      [Country.ZA]: {
        href: 'https://support.yaga.co.za/article/191-privacy-policy',
      },
      [Country.KE]: {
        href: 'https://support.yaga.co.ke/article/365-privacy-policy',
      },
      [Country.EG]: {
        href: '/privacy-policy',
      },
    }),
  REFUND: (): YagaExternalWebRoute =>
    withCountryAndLanguage({
      [Country.EE]: {
        [Language.et]: {
          href: 'https://kasutajatugi.yaga.ee/article/170-kauba-tagastamine',
        },
        [Language.en]: {
          href: 'https://support.yaga.ee/hc/en-ee/articles/28941288532765-Returns',
        },
      },
      [Country.LV]: {
        [Language.lv]: {
          href: 'https://support.shopyaga.lv/hc/lv-lv/articles/29217755503901-Atgrie%C5%A1ana',
        },
        [Language.en]: {
          href: 'https://support.shopyaga.lv/hc/en-lv/articles/28941288532765-Returns',
        },
      },
      [Country.ZA]: {
        href: 'https://support.yaga.co.za/article/100-returning-an-item-can-i-and-how',
      },
      [Country.KE]: {
        href: 'https://support.yaga.co.ke/article/362-refunds-returns',
      },
      [Country.EG]: {
        href: '',
      },
    }),
  FAQ: {
    SELLING_IS_SIMPLE: {
      1: (): YagaExternalWebRoute =>
        withCountryAndLanguage({
          [Country.EE]: {
            href: 'https://shop.yaga.ee/blogi/ideaalne-kuulutus',
          },
          [Country.LV]: {
            [Language.lv]: {
              href: 'https://support.shopyaga.lv/hc/lv-lv/categories/29217365562141-P%C4%81rdev%C4%93jiem',
            },
            [Language.en]: {
              href: 'https://support.shopyaga.lv/hc/en-lv/categories/29217365562141-P%C4%81rdev%C4%93jiem',
            },
          },
          [Country.ZA]: {
            href: 'https://shop.yaga.co.za/blog/good-listing',
          },
          [Country.KE]: {
            href: 'https://shop.yaga.co.ke/blog/good-listing',
          },
          [Country.EG]: {
            href: '',
          },
        }),
      2: (): YagaExternalWebRoute =>
        withCountryAndLanguage({
          [Country.EE]: {
            href: 'https://shop.yaga.ee/blogi/esimesed-myygid',
          },
          [Country.LV]: {
            [Language.lv]: {
              href: 'https://support.shopyaga.lv/hc/lv-lv/categories/29217365562141-P%C4%81rdev%C4%93jiem',
            },
            [Language.en]: {
              href: 'https://support.shopyaga.lv/hc/en-lv/categories/29217365562141-P%C4%81rdev%C4%93jiem',
            },
          },
          [Country.ZA]: {
            href: 'https://shop.yaga.co.za/blog/first-sales',
          },
          [Country.KE]: {
            href: 'https://shop.yaga.co.ke/blog/first-sales',
          },
          [Country.EG]: {
            href: '',
          },
        }),
      3: (): YagaExternalWebRoute =>
        withCountryAndLanguage({
          [Country.EE]: {
            href: 'https://shop.yaga.ee/blogi/sooduskoodid',
          },
          [Country.LV]: {
            [Language.lv]: {
              href: 'https://support.shopyaga.lv/hc/lv-lv/articles/29274489648029-Atlai%C5%BEu-kodi',
            },
            [Language.en]: {
              href: 'https://support.shopyaga.lv/hc/en-lv/articles/29274489648029-Atlai%C5%BEu-kodi',
            },
          },
          [Country.ZA]: {
            href: 'https://shop.yaga.co.za/blog/promo-codes',
          },
          [Country.KE]: {
            href: 'https://shop.yaga.co.ke/blog/promo-codes',
          },
          [Country.EG]: {
            href: '',
          },
        }),
    },
    SAFE_AND_SECURE: {
      1: (): YagaExternalWebRoute =>
        withCountryAndLanguage({
          [Country.EE]: {
            href: 'https://shop.yaga.ee/app',
          },
          [Country.LV]: {
            [Language.lv]: {
              href: 'https://support.shopyaga.lv/hc/lv-lv/articles/29403333947165-Nevaru-atrast-Yaga-lietotni-App-Store-Google-Play',
            },
            [Language.en]: {
              href: 'https://support.shopyaga.lv/hc/en-lv/articles/29403333947165-Nevaru-atrast-Yaga-lietotni-App-Store-Google-Play',
            },
          },
          [Country.ZA]: {
            href: 'https://shop.yaga.co.za/blog/shopping',
          },
          [Country.KE]: {
            href: 'https://shop.yaga.co.ke/blog/shopping',
          },
          [Country.EG]: {
            href: '',
          },
        }),
      2: (): YagaExternalWebRoute =>
        withCountryAndLanguage({
          [Country.EE]: {
            href: 'https://shop.yaga.ee/blogi/yaga-konto',
          },
          [Country.LV]: {
            [Language.lv]: {
              href: 'https://support.shopyaga.lv/hc/lv-lv/categories/29217371463837-Pirc%C4%93jiem#',
            },
            [Language.en]: {
              href: 'https://support.shopyaga.lv/hc/en-lv/categories/29217371463837-Pirc%C4%93jiem#',
            },
          },
          [Country.ZA]: {
            href: 'https://shop.yaga.co.za/blog/delivery',
          },
          [Country.KE]: {
            href: 'https://shop.yaga.co.ke/blog/buyer-protection',
          },
          [Country.EG]: {
            href: '',
          },
        }),
      3: (): YagaExternalWebRoute =>
        withCountryAndLanguage({
          [Country.EE]: {
            href: 'https://shop.yaga.ee/blogi/kordusost',
          },
          [Country.LV]: {
            [Language.lv]: {
              href: 'https://support.shopyaga.lv/hc/lv-lv/articles/29248502663709-Komplekt%C4%93%C5%A1ana-vair%C4%81ku-pre%C4%8Du-nos%C5%ABt%C4%AB%C5%A1ana-vien%C4%81-s%C5%ABt%C4%ABjum%C4%81',
            },
            [Language.en]: {
              href: 'https://support.shopyaga.lv/hc/en-lv/articles/29248502663709-How-to-buy-multiple-items-with-one-delivery-fee-bundling',
            },
          },
          [Country.ZA]: {
            href: 'https://shop.yaga.co.za/blog/bundling',
          },
          [Country.KE]: {
            href: 'https://shop.yaga.co.ke/blog/bundling',
          },
          [Country.EG]: {
            href: '',
          },
        }),
    },
    BUYER_PROTECTION_FEE: (): YagaExternalWebRoute =>
      withCountryAndLanguage({
        [Country.EE]: {
          [Language.et]: {
            href: 'https://kasutajatugi.yaga.ee/article/431-mis-on-ostukaitse',
          },
          [Language.en]: {
            href: 'https://kasutajatugi.yaga.ee/hc/en-ee/articles/22781480024989-What-is-Buyer-Protection-fee',
          },
        },
        [Country.LV]: {
          [Language.lv]: {
            href: 'https://support.shopyaga.lv/hc/lv-lv/articles/29347259532189-Kas-ir-Pirc%C4%93ja-aizsardz%C4%ABbas-maksa',
          },
          [Language.en]: {
            href: 'https://support.shopyaga.lv/hc/en-lv/articles/29347259532189-Kas-ir-Pirc%C4%93ja-aizsardz%C4%ABbas-maksa',
          },
        },
        [Country.ZA]: {
          href: 'https://support.yaga.co.za/article/390-buyer-protection',
        },
        [Country.KE]: {
          href: 'https://support.yaga.co.ke/hc/en-us/articles/22454397077917-Buyer-Protection',
        },
        [Country.EG]: {
          href: '',
        },
      }),
  },
  REPORT_SELLER_PROBLEM_ITEM_PAID: (): YagaExternalWebRoute =>
    withCountryAndLanguage({
      [Country.EE]: {
        [Language.et]: {
          href: 'https://kasutajatugi.yaga.ee/hc/et-ee/sections/22937980429725-Minu-tellimus-saatmise-ootel',
        },
        [Language.en]: {
          href: 'https://kasutajatugi.yaga.ee/hc/en-ee/sections/22937980429725-My-pending-order',
        },
      },
      [Country.LV]: {
        [Language.lv]: {
          href: 'https://support.shopyaga.lv/hc/lv-lv/sections/29217450735389-My-pending-order',
        },
        [Language.en]: {
          href: 'https://support.shopyaga.lv/hc/en-lv/sections/29217450735389-My-pending-order',
        },
      },
      [Country.ZA]: {
        href: 'https://yagasouthafrica.zendesk.com/hc/en-us/sections/22458675035421-My-pending-order',
      },
      [Country.KE]: {
        href: 'https://support.yaga.co.ke/hc/en-us/sections/22455102287133-My-pending-order',
      },
      [Country.EG]: {
        href: 'https://support.yaga-egypt.com/hc/en-us/requests/new',
      },
    }),
  REPORT_SELLER_PROBLEM_ITEM_IN_TRANSIT: (): YagaExternalWebRoute =>
    withCountryAndLanguage({
      [Country.EE]: {
        [Language.et]: {
          href: 'https://kasutajatugi.yaga.ee/hc/et-ee/sections/22781748778781-Minu-tellimus-teele-pandud',
        },
        [Language.en]: {
          href: 'https://kasutajatugi.yaga.ee/hc/en-ee/sections/22781748778781-My-shipped-order',
        },
      },
      [Country.LV]: {
        [Language.lv]: {
          href: 'https://support.shopyaga.lv/hc/lv-lv/sections/29217474481309-My-shipped-order',
        },
        [Language.en]: {
          href: 'https://support.shopyaga.lv/hc/en-lv/sections/29217474481309-My-shipped-order',
        },
      },
      [Country.ZA]: {
        href: 'https://yagasouthafrica.zendesk.com/hc/en-us/sections/22920941970589-My-shipped-order',
      },
      [Country.KE]: {
        href: 'https://support.yaga.co.ke/hc/en-us/sections/22936682668445-My-shipped-order',
      },
      [Country.EG]: {
        href: 'https://support.yaga-egypt.com/hc/en-us/requests/new',
      },
    }),
  REPORT_BUYER_PROBLEM_ITEM_PAID: (): YagaExternalWebRoute =>
    withCountryAndLanguage({
      [Country.EE]: {
        [Language.et]: {
          href: 'https://kasutajatugi.yaga.ee/hc/et-ee/sections/22938281956253-Minu-ost-saatmise-ootel',
        },
        [Language.en]: {
          href: 'https://kasutajatugi.yaga.ee/hc/en-ee/sections/22938281956253-My-pending-purchase',
        },
      },
      [Country.LV]: {
        [Language.lv]: {
          href: 'https://support.shopyaga.lv/hc/lv-lv/sections/29217588838173-My-pending-purchase',
        },
        [Language.en]: {
          href: 'https://support.shopyaga.lv/hc/en-lv/sections/29217588838173-My-pending-purchase',
        },
      },
      [Country.ZA]: {
        href: 'https://yagasouthafrica.zendesk.com/hc/en-us/sections/22920768091037-My-pending-purchase',
      },
      [Country.KE]: {
        href: 'https://support.yaga.co.ke/hc/en-us/sections/22937206941341-My-pending-purchase',
      },
      [Country.EG]: {
        href: 'https://support.yaga-egypt.com/hc/en-us/requests/new',
      },
    }),
  REPORT_BUYER_PROBLEM_ITEM_IN_TRANSIT: (): YagaExternalWebRoute =>
    withCountryAndLanguage({
      [Country.EE]: {
        [Language.et]: {
          href: 'https://kasutajatugi.yaga.ee/hc/et-ee/sections/22781231261981-Minu-ost-teele-pandud',
        },
        [Language.en]: {
          href: 'https://kasutajatugi.yaga.ee/hc/en-ee/sections/22781231261981-My-delivered-purchase',
        },
      },
      [Country.LV]: {
        [Language.lv]: {
          href: 'https://support.shopyaga.lv/hc/lv-lv/sections/29217590507165-My-delivered-purchase',
        },
        [Language.en]: {
          href: 'https://support.shopyaga.lv/hc/en-lv/sections/29217590507165-My-delivered-purchase',
        },
      },
      [Country.ZA]: {
        href: 'https://yagasouthafrica.zendesk.com/hc/en-us/sections/22458183341469-My-delivered-purchase',
      },
      [Country.KE]: {
        href: 'https://support.yaga.co.ke/hc/en-us/sections/22455725041565-My-delivered-purchase',
      },
      [Country.EG]: {
        href: 'https://support.yaga-egypt.com/hc/en-us/requests/new',
      },
    }),
};

function buildProductQuery(
  rankInSearch: number | undefined,
  sourceModule: string | undefined,
  searchGuid?: string,
  recommendationId?: string,
  recommendationEngine?: string,
) {
  const parts: string[] = [];

  if (rankInSearch !== undefined) {
    parts.push(`rank=${rankInSearch + 1}`);
  }

  if (sourceModule) {
    parts.push(`source_module=${encodeURIComponent(sourceModule)}`);
  }

  if (searchGuid) {
    parts.push(`searchGuid=${encodeURIComponent(searchGuid)}`);
  }

  if (recommendationId) {
    parts.push(`recommendationId=${encodeURIComponent(recommendationId)}`);
  }

  if (recommendationEngine) {
    parts.push(`recommendationEngine=${encodeURIComponent(recommendationEngine)}`);
  }

  return parts.length ? `?${parts.join('&')}` : '';
}
